import { OPERATOR_SYMBOLS, formatPeriodLabel } from '@/types'
import type { ComprehensiveMetricRow, ComprehensivePeriodValue, GoalStatus, Timeframe } from '@/types'

// Get the period boundary (quarter/half/year) for a timestamp
function getPeriodBoundary(timestamp: number, timeframe: Timeframe): { start: number; end: number; key: string } {
  const date = new Date(timestamp * 1000)
  const year = date.getUTCFullYear()

  switch (timeframe) {
    case 'quarterly': {
      const quarter = Math.floor(date.getUTCMonth() / 3)
      const start = Date.UTC(year, quarter * 3, 1) / 1000
      const end = Date.UTC(year, quarter * 3 + 3, 0, 23, 59, 59) / 1000
      return { start, end, key: `Q${quarter + 1}-${year}` }
    }
    case 'biannual': {
      const half = date.getUTCMonth() < 6 ? 0 : 1
      const start = Date.UTC(year, half * 6, 1) / 1000
      const end = Date.UTC(year, half * 6 + 6, 0, 23, 59, 59) / 1000
      return { start, end, key: `H${half + 1}-${year}` }
    }
    case 'yearly': {
      const start = Date.UTC(year, 0, 1) / 1000
      const end = Date.UTC(year, 11, 31, 23, 59, 59) / 1000
      return { start, end, key: `${year}` }
    }
    default:
      return { start: timestamp, end: timestamp, key: String(timestamp) }
  }
}

// Group values for colspan rendering of multi-period goals
// Uses boundary-aware grouping based on actual period timestamps
function groupValuesForColspan(
  values: ComprehensivePeriodValue[],
  timeframe: Timeframe | null | undefined
): { value: ComprehensivePeriodValue; colspan: number; startIdx: number }[] {
  if (!timeframe || timeframe === 'weekly' || timeframe === 'monthly') {
    // No grouping needed - one cell per period
    return values.map((v, idx) => ({ value: v, colspan: 1, startIdx: idx }))
  }

  const grouped: { value: ComprehensivePeriodValue; colspan: number; startIdx: number }[] = []
  let i = 0

  while (i < values.length) {
    const currentPeriod = values[i]
    const boundary = getPeriodBoundary(currentPeriod.period_start, timeframe)

    // Find all consecutive periods within same boundary
    let colspan = 1
    while (i + colspan < values.length) {
      const nextPeriod = values[i + colspan]
      const nextBoundary = getPeriodBoundary(nextPeriod.period_start, timeframe)
      if (nextBoundary.key !== boundary.key) break
      colspan++
    }

    // Find the value with actual data within this group, or use first
    const groupValues = values.slice(i, i + colspan)
    const valueWithData = groupValues.find(v => v.value !== null) || groupValues[0]

    grouped.push({
      value: valueWithData,
      colspan,
      startIdx: i,
    })

    i += colspan
  }

  return grouped
}

// Split period label into two lines for consistent display in table headers
function splitPeriodLabel(label: string, timeframe: Timeframe): { line1: string; line2: string } | null {
  switch (timeframe) {
    case 'weekly': {
      // "Dec-W1" → ["Dec-", "W1"]
      const match = label.match(/^(.+-)([Ww]\d+)$/)
      return match ? { line1: match[1], line2: match[2] } : null
    }
    case 'quarterly': {
      // "Q1-2025" → ["Q1-", "2025"]
      const match = label.match(/^(Q\d+-)(\d{4})$/)
      return match ? { line1: match[1], line2: match[2] } : null
    }
    case 'biannual': {
      // "Jan-Jun '25" → ["Jan-Jun", "'25"]
      const match = label.match(/^(.+)\s+('?\d{2,4})$/)
      return match ? { line1: match[1], line2: match[2] } : null
    }
    default:
      // Monthly/Yearly - no split needed
      return null
  }
}

interface ComprehensiveScorecardTableProps {
  metrics: ComprehensiveMetricRow[]
  periods: { label: string; start: number; end: number }[]
  onRowClick?: (metric: ComprehensiveMetricRow) => void
  timeframe?: Timeframe | null // Override period labels based on timeframe
}

// Format value with unit
function formatValue(value: number | null | undefined, unit: string | null): string {
  if (value === null || value === undefined) return ''
  if (unit === '$') return `$${value.toLocaleString()}`
  if (unit === '%') return `${value}%`
  return value.toLocaleString()
}

// Humanize duration since a timestamp
function humanizeDuration(timestamp: number | null): string {
  if (!timestamp) return 'Unknown'
  const now = Date.now() / 1000
  const diffSeconds = now - timestamp

  if (diffSeconds < 60) return 'Just now'

  const minutes = Math.floor(diffSeconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`

  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`

  const years = Math.floor(months / 12)
  return `${years} year${years === 1 ? '' : 's'}`
}

// Format target based on operator
function formatTarget(row: ComprehensiveMetricRow): string {
  if (!row.operator || row.target_value === null) return '—'
  const symbol = OPERATOR_SYMBOLS[row.operator] || row.operator
  if (row.operator === 'range' && row.target_min !== null && row.target_max !== null) {
    return `${formatValue(row.target_min, row.unit)}-${formatValue(row.target_max, row.unit)}`
  }
  return `${symbol} ${formatValue(row.target_value, row.unit)}`
}

// Get cell background color based on status
function getCellBgColor(status: GoalStatus, hasValue: boolean): string {
  if (!hasValue) return 'bg-gray-50'
  switch (status) {
    case 'met': return 'bg-success-100'
    case 'missed': return 'bg-danger-100'
    default: return 'bg-gray-50'
  }
}

// Get cell text color based on status
function getCellTextColor(status: GoalStatus, hasValue: boolean): string {
  if (!hasValue) return 'text-gray-400'
  switch (status) {
    case 'met': return 'text-success-700'
    case 'missed': return 'text-danger-700'
    default: return 'text-gray-600'
  }
}

export function ComprehensiveScorecardTable({ metrics, periods, onRowClick, timeframe }: ComprehensiveScorecardTableProps) {
  if (metrics.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No metrics found. Create a metric to get started.</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Sticky column - Measurable name */}
              <th scope="col" className="sticky left-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">
                Measurable
              </th>
              <th scope="col" className="bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[70px]">
                Goal
              </th>
              {/* Period columns - most recent first (highlighted) */}
              {periods.map((period, idx) => {
                const label = timeframe ? formatPeriodLabel(period.start, timeframe) : period.label
                const split = timeframe ? splitPeriodLabel(label, timeframe) : null

                return (
                  <th
                    key={period.start}
                    scope="col"
                    className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[55px] ${
                      idx === 0 ? 'bg-brand-teal text-white' : 'text-gray-600'
                    }`}
                  >
                    {split ? (
                      <>
                        {split.line1}
                        <br />
                        {split.line2}
                      </>
                    ) : (
                      label
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {metrics.map((metric) => (
              <tr
                key={`${metric.metric_id}:${metric.goal_id ?? 'null'}`}
                onClick={() => onRowClick?.(metric)}
                className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
              >
                {/* Metric name with owner avatar */}
                <td className="sticky left-0 z-10 bg-white px-3 py-3 border-r border-gray-100">
                  <div
                    className="flex items-center gap-2 cursor-default"
                    title={`Owner: ${metric.owner_name || 'Unknown'}\nAssigned ${humanizeDuration(metric.goal_start)} ago`}
                  >
                    {metric.owner_avatar ? (
                      <img
                        src={metric.owner_avatar}
                        alt={metric.owner_name || ''}
                        className="w-6 h-6 rounded-full flex-shrink-0"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-gray-500">
                          {metric.owner_name?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <span className="font-medium text-gray-900">
                      {metric.metric_name}
                    </span>
                  </div>
                </td>
                {/* Target */}
                <td className="bg-white px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                  {formatTarget(metric)}
                </td>
                {/* Period values - with colspan for multi-period goals */}
                {(() => {
                  const metricTimeframe = metric.timeframe as Timeframe | null
                  const needsColspan = metricTimeframe && ['quarterly', 'biannual', 'yearly'].includes(metricTimeframe)

                  if (needsColspan) {
                    const grouped = groupValuesForColspan(metric.values, metricTimeframe)
                    return grouped.map(({ value: pv, colspan, startIdx }) => {
                      const hasValue = pv.value !== null
                      return (
                        <td
                          key={pv.period_start}
                          colSpan={colspan}
                          className={`px-2 py-3 text-center text-sm font-medium align-middle ${getCellBgColor(pv.status, hasValue)} ${getCellTextColor(pv.status, hasValue)} ${
                            startIdx === 0 ? 'border-l-2 border-brand-teal' : ''
                          } ${colspan > 1 ? 'bg-opacity-80' : ''}`}
                        >
                          {formatValue(pv.value, metric.unit)}
                        </td>
                      )
                    })
                  }

                  // Default: one cell per period
                  return metric.values.map((pv: ComprehensivePeriodValue, idx: number) => {
                    const hasValue = pv.value !== null
                    return (
                      <td
                        key={pv.period_start}
                        className={`px-2 py-3 text-center text-sm font-medium ${getCellBgColor(pv.status, hasValue)} ${getCellTextColor(pv.status, hasValue)} ${
                          idx === 0 ? 'border-l-2 border-brand-teal' : ''
                        }`}
                      >
                        {formatValue(pv.value, metric.unit)}
                      </td>
                    )
                  })
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
